import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, KeyRound, TriangleAlert } from 'lucide-react';
import { useState } from 'react';

import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useTimeFormat } from '../../lib/useTimeFormat';
import { useAuth } from '../auth/AuthProvider';
import { devicesKey, rotatePushKey, type Device, type PushKey } from './api';
import { toProblem } from './problem';

/**
 * RTMP reverse connection (ADR-013): generates the push key and shows what to
 * configure on the device. The key is shown once; only its hash is stored.
 */
export function PushKeyPanel({
  device,
  canManage,
}: {
  device: Device | undefined;
  canManage: boolean;
}) {
  const { request } = useAuth();
  const fmt = useTimeFormat();
  const qc = useQueryClient();
  const [shown, setShown] = useState<PushKey | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const rotate = useMutation({
    mutationFn: () => rotatePushKey(request, device?.id ?? ''),
    onSuccess: async (k) => {
      setShown(k);
      setConfirm(false);
      setFailure(null);
      await qc.invalidateQueries({ queryKey: devicesKey });
    },
    onError: (err) => {
      setConfirm(false);
      setFailure(toProblem(err).form ?? 'Não foi possível gerar a chave.');
    },
  });

  if (!device || device.connection !== 'rtmp') {
    return (
      <p className="rounded-md border border-line bg-canvas px-3 py-2 text-xs text-fg-muted">
        O dispositivo envia o vídeo ao servidor: não é preciso abrir portas no local. Salve o
        dispositivo para gerar a chave de envio.
      </p>
    );
  }

  // The drawer keeps the device as it was opened: a key generated here counts.
  const hasKey = device.has_push_key || shown !== null;

  return (
    <div className="space-y-2 rounded-md border border-line bg-canvas px-3 py-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-[13px] text-fg">
          <KeyRound aria-hidden="true" className="size-4 text-fg-muted" strokeWidth={1.75} />
          {shown
            ? 'Chave de envio gerada agora'
            : device.has_push_key && device.push_key_set_at
              ? `Chave de envio gerada ${fmt.relative(device.push_key_set_at)}`
              : 'Nenhuma chave de envio gerada'}
        </span>
        {canManage && (
          <Button
            className="h-7"
            disabled={rotate.isPending}
            onClick={() => {
              if (hasKey) setConfirm(true);
              else rotate.mutate();
            }}
          >
            {rotate.isPending ? 'Gerando…' : hasKey ? 'Gerar nova chave' : 'Gerar chave'}
          </Button>
        )}
      </div>
      {failure && (
        <p role="alert" className="text-xs text-crit">
          {failure}
        </p>
      )}
      {shown && (
        <div className="space-y-2" aria-live="polite">
          <p className="flex items-start gap-1.5 text-xs text-warn">
            <TriangleAlert aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
            Copie agora: a chave não é mostrada novamente. Se perdê-la, gere uma nova.
          </p>
          <CopyField label="Servidor (URL RTMP)" value={shown.server_url} />
          <CopyField label="Chave do stream" value={shown.stream_key} />
          <CopyField label="URL completa (para aparelhos com um único campo)" value={shown.url} />
        </div>
      )}
      {!shown && device.has_push_key && (
        <p className="text-2xs text-fg-subtle">
          Por segurança a chave não fica visível. Gere uma nova se precisar configurar o aparelho de
          novo (a anterior deixa de funcionar).
        </p>
      )}
      {confirm && (
        <ConfirmDialog
          title="Gerar nova chave de envio?"
          confirmLabel="Gerar nova chave"
          busy={rotate.isPending}
          onCancel={() => {
            setConfirm(false);
          }}
          onConfirm={() => {
            rotate.mutate();
          }}
        >
          A chave atual deixa de funcionar imediatamente: o aparelho para de enviar vídeo até ser
          configurado com a nova.
        </ConfirmDialog>
      )}
    </div>
  );
}

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <div className="text-2xs text-fg-subtle">{label}</div>
      <div className="mt-0.5 flex items-center gap-1.5">
        <code className="min-w-0 flex-1 rounded border border-line-strong bg-surface px-2 py-1 font-mono text-xs break-all text-fg">
          {value}
        </code>
        <Button
          variant="ghost"
          className="h-7 shrink-0"
          aria-label={`Copiar ${label}`}
          onClick={() => {
            void navigator.clipboard.writeText(value).then(() => {
              setCopied(true);
              window.setTimeout(() => {
                setCopied(false);
              }, 1500);
            });
          }}
        >
          {copied ? (
            <Check aria-hidden="true" className="size-4 text-ok" />
          ) : (
            <Copy aria-hidden="true" className="size-4" />
          )}
        </Button>
      </div>
    </div>
  );
}
